// Copyright (c) StrangeBee 2024

import { useLocalMessage } from '@Helpers/locale';
import { fang } from '@Helpers/observable';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import React, { ReactElement, useEffect } from 'react';
import { useState } from 'react';
import { ObservableOutput } from 'thehive-sdk';
import { downloadFile } from '@Helpers/download';
import moment from 'moment';
import { THForm } from '@Molecules/THForm/THForm';
import useValidateForm from '@Hooks/useValidateForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THRadio } from '@Molecules/Inputs/THRadio/THRadio';
import { copyToClipboard } from '@Helpers/copy';

import './ObservableExport.scss';

interface ObservableExportFormValues {
    fileFormat: FileFormatType;
    protect: boolean;
}

type FileFormatType = 'csv' | 'txt' | 'misp';

const getObservableValue = ({ attachment, data = '' }: ObservableOutput, protect: boolean): string => {
    if (attachment) {
        return attachment.name + '|' + attachment.hashes[0];
    } else {
        return protect ? fang(data) : data;
    }
};

const formatter: Record<FileFormatType, (observables: ObservableOutput[], protect: boolean) => string[]> = {
    txt: (observables, protect) => observables.map((observable) => getObservableValue(observable, protect)),
    csv: (observables, protect) =>
        observables.map((observable) => observable.dataType + ';' + getObservableValue(observable, protect)),
    misp: (observables, protect) =>
        observables.map(
            (observable) =>
                '*' +
                (observable.attachment ? 'filename|sha256' : observable.dataType) +
                '\t:' +
                getObservableValue(observable, protect),
        ),
};

interface ObservableExportProps {
    triggerElem: ReactElement;
    selectedObservables: ObservableOutput[];
}

/**
 * @deprecated Use ExportManage
 */
export const ObservableExport = ({ triggerElem, selectedObservables }: ObservableExportProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange } = useValidateForm({
        isValidInit: true,
        fieldsTouchedInit: true,
    });
    const [formattedData, setFormattedData] = useState<string[]>([]);

    useEffect(() => {
        setFormattedData(formatter['csv'](selectedObservables, true));
    }, [selectedObservables]);

    const handleCopy = (): void => {
        copyToClipboard(formattedData.join(), i('notification.copy'));
    };

    const onFinish = ({ fileFormat }: ObservableExportFormValues): void => {
        downloadFile(
            `observables-${moment().format('YYYYMMDD-HH:mmZ')}.${fileFormat}`,
            formattedData.join('\n'),
            'text/csv',
        );
    };

    return (
        <THDrawer
            title={i('observable.export.title')}
            triggerElem={triggerElem}
            className="drawer-form"
            afterVisibleChange={() => form.resetFields()}
        >
            <THForm<ObservableExportFormValues>
                form={form}
                name="observable-export-name"
                onFinish={onFinish}
                onValuesChange={(_, { fileFormat, protect }) => {
                    setFormattedData(formatter[fileFormat](selectedObservables, protect));
                    onFormValuesChange();
                }}
                initialValues={{
                    fileFormat: 'csv',
                    protect: true,
                }}
            >
                <THForm.Layout
                    footer={
                        <FormSubmitButton
                            onAnother={handleCopy}
                            anotherText={i('observable.export.clipboard')}
                            footerText={i('observable.export.save-file')}
                            disabled={!isValid}
                        />
                    }
                >
                    <THForm.Item label={i('observable.export.file-format')} name="fileFormat">
                        <THRadio.Group>
                            {['csv', 'txt', 'misp'].map((value) => (
                                <THRadio key={value} value={value}>
                                    {value}
                                </THRadio>
                            ))}
                        </THRadio.Group>
                    </THForm.Item>

                    <THForm.Item label={i('observable.export.protect')} name="protect">
                        <THRadio.Group>
                            {[true, false].map((value) => (
                                <THRadio key={value + ''} value={value}>
                                    {i(value ? 'general.yes' : 'general.no')}
                                </THRadio>
                            ))}
                        </THRadio.Group>
                    </THForm.Item>

                    <div className="observable-export__description">
                        <div className="observable-export__question"> {i('observable.export.preview')}</div>
                        <div className="observable-export__list">
                            {formattedData.map((value, index) => (
                                <div key={index}>{value}</div>
                            ))}
                        </div>
                    </div>
                </THForm.Layout>
            </THForm>
        </THDrawer>
    );
};
